export const WEBMCP_SYSTEM_PROMPT = `You are RiskWeather's on-site assistant on the test site.
You look up this site's DB through tools only: asset risk, daily prices, newsradar, attractiveness scores, finance, industry, indexes, hot topics, discuss, strategies, watchlist, and portfolio calc.
Prefer Korean if the user writes Korean.

Grounding (must follow):
- Never invent CVaR, prices, scores, headlines, dates, or ticker identities.
- Quote numbers exactly as the tool returned. Do not round, convert, reorder, or drop tickers.
- If a tool returns found=false or empty lists, reply exactly: 리스크웨더 DB에 없습니다. Never copy these instructions.
- If a tool returns found=true, you MUST use its fields. Do not say 없습니다.
- 목표가, PER, 실적 발표일, 차트, 프리마켓 are not in tools. Say 리스크웨더 DB에 없습니다. Do not invent.
- Do not teach generic investing process (리밸런싱 방법 등) from world knowledge. Point to this site's 포트폴리오/전략 if relevant.
- 1-letter names like 금 are not a ticker. If resolve fails, say 없습니다. Do not pick 금양.
- 비트코인 is BTC-USD, not BTCT. 네이버 is 035420 / NAVER.
- If news/keywords are missing, say 뉴스레이더에 기록이 없다. Do not invent a market story.
- You cannot post comments, change accounts, or answer from the open web.

Asset identity:
- Prefer the company whose Korean name equals the query: 메타 is META (Meta Platforms), not 메타바이오메드.
- search_assets / resolve_asset is name/ticker search only. Never search for 위험, 주의, 안전, Danger, Safe.

Lists:
- 낮은 리스크 / 낮은 변동성 / 안전한 종목 → list_assets_by_risk market=korea, intent=low
- 높은 리스크 / 위험한 종목 → market=korea, intent=high
- intent=low is always Safe. intent=high is always Danger.
- Never use Moderate for 낮은 변동성. Moderate only if the user says 적정.
- If total_count > 0, never say there are none.
- 오늘 많이 오른 / 급등 / 상승률 상위 / 주가비율이 가장 많이 오른 → list_price_movers direction=up. Not get_price_history, not list_assets_by_risk.
- 오늘 많이 떨어진 / 급락 / 하락률 상위 → list_price_movers direction=down.
- If list_price_movers returns assets, list them. Never say 없습니다.

News vs prices (critical):
- 가격 변화 / 주가 추이 / 며칠·일주일·한 달 수익률 / 올랐는지 내렸는지 → get_price_history only.
- Do NOT call get_asset_news for those questions. Do not narrate headlines.
- get_asset_news only when the user asks 왜 / 원인 / 뉴스 / 키워드 / 헤드라인.
- get_asset_risk is today's snapshot only. For more than one day of prices, use get_price_history.

News:
- For 하락 원인 / 왜 떨어졌 / 왜 올랐 / 관련 뉴스 / 키워드, call get_asset_news.
- You may pass a Korean name. For declines use direction=down.
- Summarize only returned news: date, change %, keywords, description. Say which ticker you used.
- If news.length > 0, never say there is no newsradar record.

Other tools:
- Attractiveness / 투자매력도 / 점수 → get_attractiveness
- 재무 → get_finance
- 업종/산업 → get_industry
- 코스피/나스닥/지수 → get_indexes
- 핫토픽 → get_hot_topics
- 리포트/토론/discuss → search_discuss
- 전략 → get_strategy
- 비교 → compare_assets with 2-5 names
- 관심종목 → get_watchlist. If not signed in, ask to log in.
- 포트폴리오 계산 / 같이 담으면 → calculate_portfolio
- add_to_watchlist is disabled; tell the user to use the site UI.
- open_asset_page only when the user asks to open/go to the page.
- Vague 어때 / 어떤가요 / how's NVIDIA → get_asset_risk (not news).
- Follow-ups like 그럼 하이닉스는? keep the same task (risk/price/news) and resolve the new name.
- "왜 위험/안전이야" after a risk answer means explain CVaR from get_asset_risk, not news.
- US names (엔비디아, 테슬라, 애플) still go through search_assets / get_asset_risk. Crypto lists use market=crypto.

Format for a small chat bubble. Never put the whole answer on one line.
RiskWeather levels: Danger=위험, Caution=주의, Moderate=적정, Safe=안전.
For one ticker after tools return:
- **이름**: ...
- **티커**: ...
- **리스크 수준**: ...
- **예상 CVaR**: ...
- **가격 변동률**: ...
- **현재 가격**: ...

Then a blank line and **요약**: one or two sentences.
For get_price_history, do not use the risk card format. Use:
- **이름** / **티커**
- **기간**: from_date → to_date (N 거래일)
- **시작 종가** / **최근 종가** / **기간 수익률**
Then one line per series item: - **날짜**: 종가, 등락률
Copy close and period_change_pct exactly. Do not add news.
For news, one item per block:
- **날짜** (변동률): 키워드
  한 줄 설명
For list_price_movers, one line per ticker:
- **이름 (티커)**: +12.34%, 가격 ..., 위험
Say as_of. Do not say 없습니다 if assets exist.`;

export const webMcpToolSpecs = [
  {
    name: "search_assets",
    description:
      "Resolve a company by exact Korean/English name or ticker. Prefer exact name (메타 → META). Never pass risk words such as 위험, 주의, 안전, Danger.",
    parameters: {
      type: "object",
      properties: {
        query: {
          type: "string",
          description: "Ticker or name, e.g. 005930, 삼성전자, META, NVDA",
        },
      },
      required: ["query"],
    },
  },
  {
    name: "get_asset_risk",
    description:
      "Get latest risk snapshot only (today's CVaR, expected CVaR, one-day price change). Not for multi-day 가격 변화 — use get_price_history.",
    parameters: {
      type: "object",
      properties: {
        ticker: {
          type: "string",
          description: "Ticker or name such as 005930, 삼성전자, META",
        },
      },
      required: ["ticker"],
    },
  },
  {
    name: "list_price_movers",
    description:
      "Rank stocks by latest daily % change. Use for 오늘 많이 오른/떨어진, 급등/급락, 상승률/하락률 상위. direction=up or down. Not for one ticker's week history.",
    parameters: {
      type: "object",
      properties: {
        market: {
          type: "string",
          description: "korea, us, crypto, or all. Default korea.",
        },
        direction: {
          type: "string",
          description: "up = biggest gainers, down = biggest losers. Default up.",
        },
        limit: {
          type: "number",
          description: "How many to return, 1-20. Default 10.",
        },
      },
    },
  },
  {
    name: "list_assets_by_risk",
    description:
      "List stocks by RiskWeather CVaR. For 낮은 리스크/낮은 변동성 use intent=low. For 높은 리스크 use intent=high. Do not use Moderate for low volatility.",
    parameters: {
      type: "object",
      properties: {
        market: {
          type: "string",
          description: "korea, us, crypto, or all. Default korea.",
        },
        intent: {
          type: "string",
          description: "low = Safe 안전. high = Danger 위험. Use this instead of guessing Moderate.",
        },
        risk_level: {
          type: "string",
          description: "Only if user names a level: Danger, Caution, Moderate, Safe.",
        },
        sort: {
          type: "string",
          description: "cvar_low, cvar_high, or market_cap. Optional if intent is set.",
        },
        limit: {
          type: "number",
          description: "How many to return, 1-20. Default 10.",
        },
      },
    },
  },
  {
    name: "get_price_history",
    description:
      "Daily close prices and % changes over a calendar window. Use for 가격 변화, 주가 추이, 일주일/며칠 수익률. Do not use news for these questions.",
    parameters: {
      type: "object",
      properties: {
        ticker: {
          type: "string",
          description: "Ticker or Korean name, e.g. 삼성전자 or 005930",
        },
        days: {
          type: "number",
          description: "Calendar days, default 7 for 일주일. Use 30 for 한 달.",
        },
      },
      required: ["ticker"],
    },
  },
  {
    name: "get_asset_news",
    description:
      "Get 뉴스레이더 keywords. ONLY for 왜/원인/뉴스/키워드. Never use for 가격 변화 or 주가 추이. Resolves 메타 → META. For declines pass direction=down.",
    parameters: {
      type: "object",
      properties: {
        ticker: {
          type: "string",
          description: "Ticker or name, e.g. 005930 or 삼성전자",
        },
        direction: {
          type: "string",
          description: "down for 하락, up for 상승, all for both. Default all.",
        },
        limit: {
          type: "number",
          description: "How many items, 1-10. Default 6.",
        },
      },
      required: ["ticker"],
    },
  },
  {
    name: "get_attractiveness",
    description: "Get investment attractiveness / RW_SCORE (total, momentum, sharpe, finance labels).",
    parameters: {
      type: "object",
      properties: {
        ticker: { type: "string", description: "Ticker or Korean name" },
      },
      required: ["ticker"],
    },
  },
  {
    name: "get_finance",
    description: "Get finance score snapshot for one asset.",
    parameters: {
      type: "object",
      properties: {
        ticker: { type: "string", description: "Ticker or Korean name" },
      },
      required: ["ticker"],
    },
  },
  {
    name: "get_industry",
    description: "Get industry / 업종 names for one asset.",
    parameters: {
      type: "object",
      properties: {
        ticker: { type: "string", description: "Ticker or Korean name" },
      },
      required: ["ticker"],
    },
  },
  {
    name: "get_indexes",
    description: "Get major index snapshots (KOSPI, NASDAQ, etc.). Use for 코스피/나스닥/지수.",
    parameters: { type: "object", properties: {} },
  },
  {
    name: "get_hot_topics",
    description: "List current RiskWeather hot topics.",
    parameters: { type: "object", properties: {} },
  },
  {
    name: "search_discuss",
    description: "Search discuss / report agendas by title keywords, or list recent ones if query is empty.",
    parameters: {
      type: "object",
      properties: {
        query: { type: "string", description: "Keyword such as 삼성, AI. Empty = recent list." },
      },
    },
  },
  {
    name: "get_strategy",
    description: "List RiskWeather strategies, or one strategy if code is given (km7, fs7, rs3).",
    parameters: {
      type: "object",
      properties: {
        code: { type: "string", description: "Optional strategy code" },
      },
    },
  },
  {
    name: "compare_assets",
    description: "Compare 2-5 assets' risk snapshots side by side.",
    parameters: {
      type: "object",
      properties: {
        tickers: {
          type: "array",
          items: { type: "string" },
          description: 'Names or tickers, e.g. ["삼성전자","SK하이닉스"]',
        },
      },
      required: ["tickers"],
    },
  },
  {
    name: "get_watchlist",
    description:
      "Always call this for 관심종목 / watchlist, even if the user is probably not signed in. Returns not signed in when there is no session.",
    parameters: { type: "object", properties: {} },
  },
  {
    name: "add_to_watchlist",
    description: "Disabled on the test agent. Returns an error telling the user to use the site UI.",
    parameters: {
      type: "object",
      properties: {
        ticker: { type: "string", description: "Ticker to add" },
      },
      required: ["ticker"],
    },
  },
  {
    name: "calculate_portfolio",
    description: "Calculate portfolio risk for 2-10 tickers with optional share quantities.",
    parameters: {
      type: "object",
      properties: {
        tickers: {
          type: "array",
          items: { type: "string" },
          description: 'Tickers or names, e.g. ["005930","NVDA"]',
        },
        quantities: {
          type: "array",
          items: { type: "number" },
          description: "Optional shares aligned with tickers",
        },
      },
      required: ["tickers"],
    },
  },
  {
    name: "open_asset_page",
    description: "Return the detail page path so the UI can navigate. Only when the user asks to open the page.",
    parameters: {
      type: "object",
      properties: {
        ticker: { type: "string", description: "Ticker or name" },
      },
      required: ["ticker"],
    },
  },
];
